import { useQuery } from "@apollo/client/react";
import { Box, Button, PrimaryButton } from "@nulogy/components";
import UnstyledLink from "components/common/UnstyledLink";
import Form from "components/controls/Form";
import StickyFooter from "components/controls/StickyFooter";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import paths from "config/routePaths";
import { GetDefaultAvailabilities } from "hooks/apollo/defaultAvailability/defaultAvailability.gql";
import useUpdateDefaultAvailabilities from "hooks/apollo/defaultAvailability/useUpdateDefaultAvailabilities";
import { GetAllShifts } from "hooks/apollo/shift/shift.gql";
import useConfirmDefaultAvailabilityChangesModal from "hooks/state/useConfirmDefaultAvailabilityChangesModal";
import useNotificationState from "hooks/state/useNotificationState";
import uniqBy from "lodash/uniqBy";
import React from "react";
import { useNavigate } from "react-router";
import AvailabilityEmptyState from "./AvailabilityEmptyState";
import ConfirmDefaultAvailabilityChangesModal from "./ConfirmDefaultAvailabilityChangesModal";
import DefaultAvailabilityTable from "./DefaultAvailabilityTable";

export default function DefaultAvailability() {
  const navigate = useNavigate();
  const { data, loading } = useQuery(GetDefaultAvailabilities);
  const { data: shiftData, loading: shiftsLoading } = useQuery(GetAllShifts);

  const leavePage = () => {
    navigate(paths.home);
  };

  const updateDefaultAvailabilities = useUpdateDefaultAvailabilities();
  const { close: closeConfirmChangesModal, open: openConfirmChangesModal } =
    useConfirmDefaultAvailabilityChangesModal();
  const { setNotification } = useNotificationState();

  if (loading || shiftsLoading) return null;

  const { defaultAvailabilities } = data;
  const lines = uniqBy(
    defaultAvailabilities.map((defaultAvailability) => defaultAvailability.line),
    "id",
  );

  const { shifts } = shiftData;

  const handleSave = (formData) =>
    updateDefaultAvailabilities(formData.defaultAvailabilities)
      .then(() =>
        setNotification({
          title: "The schedule’s availability was successfully updated.",
          type: "success",
        }),
      )
      .catch(() =>
        setNotification({
          title: "The changes were not saved. Please try again.",
          type: "danger",
        }),
      )
      .finally(() => closeConfirmChangesModal());

  const renderContent = () => {
    if (defaultAvailabilities.length === 0) return <AvailabilityEmptyState />;

    return (
      <Form initialValues={data} onSave={handleSave}>
        <Box maxWidth="1040px" minWidth="1040px">
          <div data-testid="content">
            {shifts.map((shift) => (
              <DefaultAvailabilityTable
                defaultAvailabilities={defaultAvailabilities}
                key={shift.id}
                lines={lines}
                shift={shift}
              />
            ))}
          </div>

          <StickyFooter>
            <Box paddingBottom="x2">
              <PrimaryButton data-testid="save-at-bottom" mr="x1" onClick={openConfirmChangesModal} type="button">
                Save and apply to schedule
              </PrimaryButton>
              <Button onClick={leavePage} type="button">
                Cancel
              </Button>
              <ConfirmDefaultAvailabilityChangesModal />
            </Box>
          </StickyFooter>
        </Box>
      </Form>
    );
  };

  return (
    <PageWithBreadcrumbs
      data-testid="title"
      description={
        <>
          Set the default availability per line and shift. Unchecked slots will be reflected on the schedule as
          unavailable. To accommodate day to day variation, go to{" "}
          <UnstyledLink href={paths.availabilityOverrides} style={{ fontWeight: "normal" }}>
            Availability overrides
          </UnstyledLink>
          .
        </>
      }
      title="Default availability"
    >
      {renderContent()}
    </PageWithBreadcrumbs>
  );
}
